import type { TruckRow } from '@routeback/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { useApp } from '../lib/app';

/**
 * Pick a truck from a dropdown, sorted by unit number. With several
 * terminals, the current terminal's trucks (and ones with no terminal) come
 * first and the rest are grouped under "Other terminals" — for the day a
 * truck comes over from another building.
 */
export function TruckPicker({ value, onChange, id }: { value: string | null; onChange: (id: string | null) => void; id?: string }) {
  const app = useApp();
  const trucks = useLiveQuery(
    async () => (await app.db.table<TruckRow, string>('trucks').where('company_id').equals(app.company.id).toArray())
      .filter((t) => !t.deleted_at && t.active)
      .sort((a, b) => a.unit_number.localeCompare(b.unit_number, undefined, { numeric: true })),
    [app.db, app.company.id],
    [] as TruckRow[],
  );
  if (trucks.length === 0) return <p className="muted small" style={{ margin: 0 }}>No trucks set up yet — ask your manager.</p>;

  const scoped = app.multiTerminal && app.terminal !== 'all';
  const here = scoped ? trucks.filter((t) => !t.terminal_id || t.terminal_id === app.terminal) : trucks;
  const other = scoped ? trucks.filter((t) => t.terminal_id && t.terminal_id !== app.terminal) : [];
  const opt = (t: TruckRow) => <option key={t.id} value={t.id}>{t.unit_number}</option>;
  return (
    <select id={id} value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} style={{ fontSize: 17 }}>
      <option value="">Select unit number…</option>
      {other.length ? (
        <>
          <optgroup label={app.terminals.find((t) => t.id === app.terminal)?.name ?? 'This terminal'}>{here.map(opt)}</optgroup>
          <optgroup label="Other terminals">{other.map(opt)}</optgroup>
        </>
      ) : here.map(opt)}
    </select>
  );
}
